import React, { useEffect, useRef, useState } from 'react';
import { Offcanvas, Spinner } from 'react-bootstrap';
import styles from './ProfitPlusSyncHelpDrawer.module.scss';

/** Lazy chunk per GIF — loaded on first open (cached in-memory for the session). */
const profitPlusSyncHelpGifLoaders = {
  clients: () => import('../../../res/gifs/refresh_clients_profit_plus_2k8.gif'),
  products: () => import('../../../res/gifs/refresh_products_profit_plus_2k8.gif'),
  salesPeople: () => import('../../../res/gifs/refresh_sales_people_profit_plus_2k8.gif'),
  orderInvoiceFromSale: () =>
    import('../../../res/gifs/generate_invoice_from_sale_once_synched.gif'),
} as const;

export type ProfitPlusSyncHelpGifKey = keyof typeof profitPlusSyncHelpGifLoaders;

interface ProfitPlusSyncHelpDrawerProps {
  show: boolean;
  onHide: () => void;
  title: string;
  intro: string;
  bullets: string[];
  gif: ProfitPlusSyncHelpGifKey;
  gifAlt: string;
}

const ProfitPlusSyncHelpDrawer: React.FC<ProfitPlusSyncHelpDrawerProps> = ({
  show,
  onHide,
  title,
  intro,
  bullets,
  gif,
  gifAlt,
}) => {
  const gifUrlCacheRef = useRef<Map<ProfitPlusSyncHelpGifKey, string>>(new Map());
  const [gifSrc, setGifSrc] = useState<string | null>(null);
  const [gifFailed, setGifFailed] = useState(false);

  useEffect(() => {
    if (!show) return;

    const cached = gifUrlCacheRef.current.get(gif);
    if (cached) {
      setGifSrc(cached);
      setGifFailed(false);
      return;
    }

    setGifSrc(null);
    setGifFailed(false);
    let cancelled = false;

    profitPlusSyncHelpGifLoaders[gif]()
      .then(m => {
        if (cancelled) return;
        gifUrlCacheRef.current.set(gif, m.default);
        setGifSrc(m.default);
      })
      .catch(() => {
        if (!cancelled) setGifFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [show, gif]);

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      className={styles.helpDrawer}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>{title}</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <p className={styles.helpMessage}>{intro}</p>
        <ul className={styles.helpList}>
          {bullets.map(item => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        {gifFailed ? (
          <p className="text-danger small mb-0">No se pudo cargar la guía visual.</p>
        ) : gifSrc ? (
          <img
            src={gifSrc}
            alt={gifAlt}
            className={styles.helpImage}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div
            className={`d-flex justify-content-center align-items-center py-5 ${styles.gifPlaceholder}`}
          >
            <Spinner animation="border" role="status" variant="secondary">
              <span className="visually-hidden">Cargando guía…</span>
            </Spinner>
          </div>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default ProfitPlusSyncHelpDrawer;
